/* global BigInt */
import { ethers } from 'ethers';
import blockchainApi from '../api/blockchainApi';
import { getWCProvider, bringWalletToFront } from './walletConnect';
import { getAddChainParams } from './chainParams';

const RPC_PROXY = 'https://api.duotetra.quest/promos/blockchain/rpc';

const ERC20_ABI = [
    'function decimals() view returns (uint8)',
    'function symbol() view returns (string)',
    'function balanceOf(address) view returns (uint256)',
];

const POOL_CLAIM_ABI = [
    'function claim(uint256 promoId, uint256 amount, uint256 nonce, bytes signature)',
];

// Topic для ERC20 Transfer event: keccak256("Transfer(address,address,uint256)")
const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

// Достаём Transfer event из receipt.logs, фильтруем по адресу токена и winner'у
const findTransferToWinner = (receipt, tokenAddress, winnerAddress) => {
    const tokenLower = tokenAddress.toLowerCase();
    const winnerLower = winnerAddress.toLowerCase();
    for (const log of receipt.logs || []) {
        if ((log.address || '').toLowerCase() !== tokenLower) continue;
        if ((log.topics?.[0] || '') !== TRANSFER_TOPIC) continue;
        const to = '0x' + (log.topics[2] || '').slice(-40);
        if (to.toLowerCase() !== winnerLower) continue;
        const from = '0x' + (log.topics[1] || '').slice(-40);
        const amount = BigInt(log.data);
        return { from, to, amount };
    }
    return null;
};

const withTimeout = (p, ms, msg) =>
    Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);

// Получаем signer (MetaMask / injected → WalletConnect-сессия)
const getSigner = async () => {
    if (typeof window !== 'undefined' && window.ethereum) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        await withTimeout(
            provider.send('eth_requestAccounts', []),
            15000,
            'Wallet did not respond. Unlock your wallet and try again.',
        );
        return provider.getSigner();
    }

    const wc = await withTimeout(getWCProvider(), 10000, 'Failed to initialize WalletConnect');
    if (wc && (wc.connected || wc.session)) {
        const provider = new ethers.BrowserProvider(wc);
        return provider.getSigner();
    }

    // Сессия могла умереть при перезагрузке вебвью — пробуем восстановить,
    // чтобы юзеру не пришлось вручную переподключать кошелёк ради клейма.
    try {
        await withTimeout(wc.connect(), 180000, 'Wallet did not respond to connection request');
        if (wc.connected || wc.session) {
            return new ethers.BrowserProvider(wc).getSigner();
        }
    } catch (e) {
        if (/did not respond/i.test(e?.message || '')) throw e;
    }

    throw new Error('Wallet not connected. Open Wallet page and connect first.');
};

const switchChain = async (provider, chainId) => {
    const hex = '0x' + chainId.toString(16);
    try {
        await Promise.race([
            provider.send('wallet_switchEthereumChain', [{ chainId: hex }]),
            new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000)),
        ]);
    } catch (err) {
        if (err?.message === 'timeout') return;
        if (err?.code === 4902 || /Unrecognized chain|wallet_addEthereumChain/i.test(err?.message || '')) {
            // Параметры по chainId, а не захардкоженный Hardhat (см. chainParams.js)
            const addParams = getAddChainParams(chainId, RPC_PROXY);
            if (addParams) {
                try { await provider.send('wallet_addEthereumChain', [addParams]); } catch {}
            }
        }
    }
};

/**
 * Полный on-chain claim flow:
 *   1. Получить signer кошелька юзера → его адрес = winnerAddress
 *   2. Дёрнуть бэк за подписью signClaim({ winnerAddress, promoId, amount(raw), nonce })
 *   3. Вызвать GiveawayPool.claim(promoId, amount(raw), nonce, signature)
 *   4. Уведомить бэкенд через notifyClaimed
 *
 * @param {object} params
 * @param {number|string} params.promoId
 * @param {string|number} params.humanAmount   — сумма в человеческих единицах (например '3000' или '1.5')
 * @param {string} params.tokenAddress         — адрес ERC20 токена (для decimals)
 * @param {object} [params.config]             — кешированный blockchainConfig {chainId, giveawayPool}; если нет — получим
 * @returns {Promise<{ txHash: string, signature: string, winnerAddress: string }>}
 */
export async function executeClaim({ promoId, humanAmount, tokenAddress, config }) {
    if (!promoId) throw new Error('promoId is required');
    if (!tokenAddress) throw new Error('tokenAddress is required');
    const amountStr = String(humanAmount ?? '0');
    if (Number(amountStr) <= 0) throw new Error('Invalid amount');

    // 1. Signer + winner address
    const signer = await getSigner();
    const winnerAddress = await signer.getAddress();

    // 2. Config (giveawayPool address, chainId)
    const cfg = config || ((await blockchainApi.getConfig())?.data) || {};
    if (!cfg?.giveawayPool) throw new Error('Blockchain config not loaded');

    // 3. Switch chain (best-effort)
    try { await switchChain(signer.provider, cfg.chainId || 31337); } catch {}

    // 4. Конвертируем human-сумму в raw (token decimals)
    const readProvider = new ethers.JsonRpcProvider(RPC_PROXY);
    const erc20 = new ethers.Contract(tokenAddress, ERC20_ABI, readProvider);
    const [decimals, symbol] = await Promise.all([
        erc20.decimals(),
        erc20.symbol().catch(() => 'TOKEN'),
    ]);
    const rawAmount = ethers.parseUnits(amountStr, decimals).toString();

    // ── PRE-CHECK: запоминаем баланс юзера ДО claim'а ────────────────────────
    const balanceBefore = await erc20.balanceOf(winnerAddress);
    // В лог не пишем адрес и баланс победителя — это персональные данные,
    // а консоль в вебвью может быть видна посторонним. Оставляем только то,
    // что нужно для диагностики выплаты.
    console.log('🔍 [claim] pre-check:', {
        symbol,
        decimals: Number(decimals),
        expectedHuman: amountStr,
    });

    // 5. Подпись от бэка (с правильным winnerAddress!). Nonce выдаёт СЕРВЕР
    // (детерминированный, по награде) — подпись сходится только с ним.
    // Свой шлём лишь как фолбэк на время выката старого бэка.
    const clientNonce = Date.now();
    const sigRes = await blockchainApi.signClaim({
        winnerAddress,
        promoId,
        amount: rawAmount,
        nonce: clientNonce,
    });
    const signature = sigRes?.data?.signature || sigRes?.signature;
    if (!signature) throw new Error('Backend did not return signature');
    const nonce = sigRes?.data?.nonce ?? sigRes?.nonce ?? clientNonce;

    // 6. Вызов contract.claim — токены перейдут на адрес msg.sender (= winnerAddress)
    const pool = new ethers.Contract(cfg.giveawayPool, POOL_CLAIM_ABI, signer);
    const txReq = pool.claim(promoId, rawAmount, nonce, signature);
    setTimeout(bringWalletToFront, 700);
    const tx = await txReq;
    const receipt = await tx.wait();

    // ── POST-CHECK: верифицируем результат on-chain ──────────────────────────
    const balanceAfter = await erc20.balanceOf(winnerAddress);
    const delta = balanceAfter - balanceBefore;
    const expected = BigInt(rawAmount);
    const transferEvt = findTransferToWinner(receipt, tokenAddress, winnerAddress);

    const verification = {
        ok: false,
        txHash: receipt.hash,
        symbol,
        winnerAddress,
        balanceBefore: ethers.formatUnits(balanceBefore, decimals),
        balanceAfter: ethers.formatUnits(balanceAfter, decimals),
        delta: ethers.formatUnits(delta, decimals),
        expected: amountStr,
        deltaMatches: delta === expected,
        transferEvent: transferEvt
            ? {
                  from: transferEvt.from,
                  to: transferEvt.to,
                  amount: ethers.formatUnits(transferEvt.amount, decimals),
                  amountMatches: transferEvt.amount === expected,
              }
            : null,
    };
    verification.ok =
        verification.deltaMatches &&
        verification.transferEvent != null &&
        verification.transferEvent.amountMatches;

    if (verification.ok) {
        console.log('✅ [claim] VERIFIED:', verification);
    } else {
        console.error('❌ [claim] VERIFICATION FAILED:', verification);
    }

    // 7. Уведомляем бэк (некритично — если упадёт, claim уже прошёл on-chain)
    try {
        await blockchainApi.notifyClaimed({
            promoId,
            winner: winnerAddress,
            amount: rawAmount,
            amountHuman: amountStr,
            nonce,
            txHash: receipt.hash,
            blockNumber: receipt.blockNumber,
        });
    } catch (notifyErr) {
        console.warn('notifyClaimed failed (non-critical):', notifyErr?.message || notifyErr);
    }

    return { txHash: receipt.hash, signature, winnerAddress, verification };
}
